import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import OrderRequestsPanel from '../components/orders/OrderRequestsPanel';
import OrdersReadOnly from '../components/orders/OrdersReadOnly';
import ReceptionPanel from '../components/orders/ReceptionPanel';
import MyOrderEditor from '../components/orders/MyOrderEditor';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../services/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ALIAS = { demandes: 'consolidation', directe: 'mes' };
const TITLES = {
  mes: ['Mes commandes', 'Saisissez votre commande par catégorie, enregistrez-la puis validez-la : elle part en consolidation.'],
  consolidation: ['Consolidation', 'Les commandes validées par les collaborateurs sont regroupées par journée de livraison ; ajustez puis validez la commande du jour.'],
  reception: ['Réception', 'Contrôlez les livraisons par Swipe : quantités reçues et remarques.'],
  lecture: ['Commande', 'Consultation des commandes enregistrées.'],
};

const CommandePage = () => {
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const canConsolidate = hasPermission('view_all_order_requests');
  const canOrder = hasPermission('create_order_request') || hasPermission('create_order');
  const canReceive = hasPermission('receive_order');
  const canViewOrders = !canOrder && !canConsolidate && hasPermission('view_orders');
  const available = [canConsolidate && 'consolidation', canOrder && 'mes', canReceive && 'reception', canViewOrders && 'lecture'].filter(Boolean);
  const requested = searchParams.get('tab');
  const wanted = ALIAS[requested] || requested;
  const mode = available.includes(wanted) ? wanted : (available.includes('mes') ? 'mes' : available[0] || 'mes');
  const setMode = (m) => setSearchParams(m === 'mes' ? {} : { tab: m });

  const canSync = hasPermission('manage_team') || hasPermission('manage_system');
  const [syncState, setSyncState] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const loadSyncStatus = useCallback(() => axios.get(`${API}/orders/sync-status`).then(r => setSyncState(r.data.data)).catch(() => {}), []);
  useEffect(() => { if (canSync) loadSyncStatus(); }, [canSync, loadSyncStatus]);
  const handleSyncOdoo = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const d = (await axios.post(`${API}/orders/sync-odoo`)).data.data;
      toast.success(`Odoo synchronisé : ${d.total_active} produits (${d.created} nouveaux, ${d.updated} mis à jour)`);
      await loadSyncStatus();
    } catch (err) { toast.error(formatApiError(err)); } finally { setSyncing(false); }
  };

  const tabCls = (m) => `flex-1 sm:flex-none h-10 px-2 sm:px-5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap ${mode === m ? 'bg-white text-gray-900 shadow' : 'text-gray-600'}`;
  const [title, subtitle] = TITLES[mode];

  return (
    <div className="min-h-screen p-3 md:p-8 pb-24 md:pb-8" data-testid="commande-page">
      <div className="max-w-6xl mx-auto mb-4">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-gray-900 tracking-tight mb-1">{title}</h1>
        <p className="text-gray-500 text-sm mb-4">{subtitle}</p>
        {available.length > 1 && (
          <div className="flex gap-1.5 mb-4 bg-gray-100 rounded-xl p-1 w-full sm:w-auto" data-testid="commande-mode-tabs">
            {canConsolidate && <button type="button" onClick={() => setMode('consolidation')} className={tabCls('consolidation')} data-testid="commande-tab-demandes">Consolidation</button>}
            {canOrder && <button type="button" onClick={() => setMode('mes')} className={tabCls('mes')} data-testid="commande-tab-directe">Mes commandes</button>}
            {canReceive && <button type="button" onClick={() => setMode('reception')} className={tabCls('reception')} data-testid="commande-tab-reception">Réception</button>}
            {canViewOrders && <button type="button" onClick={() => setMode('lecture')} className={tabCls('lecture')} data-testid="commande-tab-lecture">Commandes enregistrées</button>}
          </div>
        )}
      </div>
      {mode === 'mes' && <MyOrderEditor canSync={canSync} syncState={syncState} syncing={syncing} onSync={handleSyncOdoo} />}
      {mode === 'consolidation' && <OrderRequestsPanel initialTab={searchParams.get('sub') || undefined} />}
      {mode === 'reception' && <ReceptionPanel />}
      {mode === 'lecture' && <OrdersReadOnly />}
    </div>
  );
};

export default CommandePage;
